// AYA PRO — Parcours d'inscription complet (maquettes Réf 4, 2, 8).
// profil → secteur → métier → formulaire → vérification tél → identifiants → lien d'activation.
// Brouillon persistant (sans mot de passe). MODE DEV : code SMS + lien affichés (aucun fournisseur).
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Store, Users, ChevronRight, Search, Phone, Mail, Lock, Eye, EyeOff, ShieldCheck, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { AyaProLogo } from "@/components/pro/AyaProLogo";
import { SECTORS, findSector, findMetier } from "./ayaproSectors";
import "./ayapro.css";

const API = process.env.REACT_APP_BACKEND_URL;
const DRAFT_KEY = "aya_pro_signup_v1";
const FLOW = ["profile", "sector", "metier", "form", "verify", "credentials", "activation"];

const Stepper = ({ steps, current }) => (
  <div className="ap-steps" data-testid="ayapro-stepper">
    {steps.map((s, i) => (
      <div key={s} style={{ display: "contents" }}>
        {i > 0 && <span className={`ap-stepline ${i <= current ? "done" : ""}`} />}
        <div className={`ap-step ${i < current ? "done" : i === current ? "current" : ""}`}>
          <span className="dot">{i < current ? "✓" : i + 1}</span>
          <span className="lbl">{s}</span>
        </div>
      </div>
    ))}
  </div>
);

const PwChecks = ({ pw }) => {
  const reqs = [
    ["Au moins 8 caractères", pw.length >= 8],
    ["Une lettre minuscule", /[a-z]/.test(pw)],
    ["Une lettre majuscule", /[A-Z]/.test(pw)],
    ["Un chiffre ou un caractère spécial", /[\d\W]/.test(pw)],
  ];
  return (
    <div className="ap-reqbox">
      <span className="shield"><ShieldCheck size={18} /></span>
      <div style={{ flex: 1 }}>
        <h5>Votre mot de passe doit contenir :</h5>
        <div className="ap-reqs">
          {reqs.map(([t, ok]) => (
            <span key={t} className={`ap-req ${ok ? "ok" : ""}`}>{ok ? "✓" : "○"} {t}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

const pwScore = (pw) => [pw.length >= 8, /[a-z]/.test(pw) && /[A-Z]/.test(pw), /[\d\W]/.test(pw) && pw.length >= 10].filter(Boolean).length;
const pwValid = (pw) => pw.length >= 8 && /[a-z]/.test(pw) && /[A-Z]/.test(pw) && /[\d\W]/.test(pw);

export default function AyaProSignupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("profile");
  const [sector, setSector] = useState(null);
  const [metier, setMetier] = useState(null);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ business_name: "", owner_name: "", phone: "", email: "", password: "", terms: false });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [devCode, setDevCode] = useState(null);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const [cred, setCred] = useState({ email: "", pw: "", pw2: "" });
  const [showCred, setShowCred] = useState(false);
  const [activation, setActivation] = useState(null);

  // Reprise du brouillon (jamais de mot de passe stocké).
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (s?.step && FLOW.includes(s.step)) {
        setSector(s.sector || null); setMetier(s.metier || null);
        setF((p) => ({ ...p, ...s.f, password: "" }));
        setAccountId(s.accountId || null); setDevCode(s.devCode || null); setActivation(s.activation || null);
        setStep(s.step);
        if (s.step !== "profile") toast.success("Reprise de votre inscription");
      }
    } catch { /* brouillon illisible */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, sector, metier, f: { ...f, password: "" }, accountId, devCode, activation })); } catch { /* quota */ }
  }, [step, sector, metier, f, accountId, devCode, activation]);

  const go = (s) => { setStep(s); window.scrollTo(0, 0); };
  const back = () => {
    const i = FLOW.indexOf(step);
    if (i <= 0) { navigate("/pro/bienvenue"); return; }
    if (step === "credentials" || step === "activation") { navigate("/pro/bienvenue"); return; }
    go(FLOW[i - 1]);
  };

  const sec = findSector(sector);
  const met = findMetier(sector, metier);
  const shownSectors = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return nq ? SECTORS.filter((s) => `${s.label} ${s.metiers.map((m) => m.label).join(" ")}`.toLowerCase().includes(nq)) : SECTORS;
  }, [q]);
  const shownMetiers = useMemo(() => {
    const nq = q.trim().toLowerCase();
    const base = sec?.metiers || [];
    return nq ? base.filter((m) => `${m.label} ${m.sub}`.toLowerCase().includes(nq)) : base;
  }, [q, sec]);

  const register = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/ayapro/register`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_name: f.business_name, owner_name: f.owner_name, phone: f.phone, email: f.email || null, password: f.password, sector, metier, accept_terms: f.terms }),
      });
      const j = await r.json();
      if (!j.ok) {
        const msg = j.error === "phone_already_registered" ? "Ce numéro a déjà un compte AYA PRO actif — connectez-vous"
          : j.error === "invalid_phone" ? "Numéro de téléphone invalide" : "Vérifiez vos informations";
        throw new Error(msg);
      }
      setAccountId(j.account_id); setDevCode(j.dev_code || null); setOtp(["", "", "", "", "", ""]);
      go("verify");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const setOtpAt = (i, v) => {
    const d = v.replace(/\D/g, "").slice(-1);
    setOtp((p) => { const n = [...p]; n[i] = d; return n; });
    if (d && i < 5) otpRefs.current[i + 1]?.focus();
  };
  const verify = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/ayapro/verify-phone`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: accountId, code: otp.join("") }),
      });
      const j = await r.json();
      if (!j.ok) {
        const msg = j.error === "code_expired" ? "Code expiré — renvoyez un code" : j.error === "too_many_attempts" ? "Trop de tentatives — renvoyez un code" : "Code incorrect";
        throw new Error(msg);
      }
      toast.success("Numéro vérifié ✓");
      setCred((p) => ({ ...p, email: f.email || "" }));
      go("credentials");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const resend = async () => {
    const r = await fetch(`${API}/api/ayapro/resend-code`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ account_id: accountId }) });
    const j = await r.json();
    if (j.ok) { setDevCode(j.dev_code || null); setOtp(["", "", "", "", "", ""]); toast.success("Nouveau code envoyé"); }
    else toast.error("Impossible de renvoyer le code");
  };

  const saveCredentials = async () => {
    if (cred.pw !== cred.pw2) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/ayapro/set-credentials`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: accountId, password: cred.pw, email: cred.email || null }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error("Impossible d'enregistrer vos identifiants");
      setActivation({ path: j.activation_path, hours: j.expires_in_hours, phone: j.phone });
      go("activation");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}${activation.path}`); toast.success("Lien copié"); } catch { toast.error("Copie impossible"); }
  };

  const score = pwScore(cred.pw);
  const formOk = f.business_name.trim().length >= 2 && f.owner_name.trim().length >= 2 && f.phone.replace(/\D/g, "").length >= 8 && pwValid(f.password) && f.terms;

  return (
    <div className="ap-page" data-testid="ayapro-signup-page">
      <div className="ap-wrap">
        <div className="ap-head">
          <button className="ap-back" onClick={back} data-testid="ayapro-signup-back"><ArrowLeft size={15} /> Retour</button>
          <AyaProLogo variant="onboarding" onLight />
          <span className="ap-lang">FR ▾</span>
        </div>

        {/* ── Vous êtes ? ── */}
        {step === "profile" && (
          <div data-testid="ayapro-step-profile">
            <h1 className="ap-h1" style={{ textAlign: "left" }}>Vous êtes ?</h1>
            <p className="ap-sub" style={{ textAlign: "left", margin: "6px 0 0" }}>Choisissez votre profil pour commencer.</p>
            <button className="ap-choice active" onClick={() => go("sector")} data-testid="ayapro-profile-pro">
              <span className="ic"><Store size={23} color="#0E8A4F" /></span>
              <div><h3>Un professionnel ou une entreprise</h3><p>Commerçant, artisan, prestataire de services, société, etc.</p></div>
              <ChevronRight size={18} className="chev" />
            </button>
            <button className="ap-choice" onClick={() => navigate("/search")} data-testid="ayapro-profile-particulier">
              <span className="ic"><Users size={23} color="#64748B" /></span>
              <div><h3>Un particulier</h3><p>Pour utiliser les services AYA (recherche, réservation, achat, etc.)</p></div>
              <ChevronRight size={18} className="chev" />
            </button>
            <img src="/aya/ayapro/merchant-woman.jpg" alt="Professionnels sur AYA PRO" className="ap-herophoto" />
            <div className="ap-badge" style={{ marginTop: 12 }}>✓ Rejoignez des milliers de professionnels déjà sur AYA PRO !</div>
          </div>
        )}

        {/* ── Secteur ── */}
        {step === "sector" && (
          <div data-testid="ayapro-step-sector">
            <h1 className="ap-h1" style={{ textAlign: "left" }}>Quelle activité souhaitez-vous inscrire ?</h1>
            <p className="ap-sub" style={{ textAlign: "left", margin: "6px 0 0" }}>Sélectionnez votre secteur pour commencer.</p>
            <div className="ap-search"><Search size={14} color="#9CA3AF" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un métier…" data-testid="ayapro-sector-search" /></div>
            <div className="ap-sectors">
              {shownSectors.map((s) => (
                <button key={s.id} className={`ap-sector ${sector === s.id ? "active" : ""}`} style={{ background: s.bg }} onClick={() => { setSector(s.id); setMetier(null); setQ(""); go("metier"); }} data-testid={`ayapro-sector-${s.id}`}>
                  <span className="ic">{s.icon}</span><span className="lbl">{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Métier ── */}
        {step === "metier" && sec && (
          <div data-testid="ayapro-step-metier">
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8 }}>
              <span style={{ width: 48, height: 48, borderRadius: 14, background: sec.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 23 }}>{sec.icon}</span>
              <div><h1 className="ap-h1" style={{ textAlign: "left", margin: 0, fontSize: 20 }}>{sec.label}</h1><p className="ap-sub" style={{ textAlign: "left", margin: "2px 0 0" }}>Sélectionnez votre métier.</p></div>
            </div>
            <div className="ap-search"><Search size={14} color="#9CA3AF" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un métier…" data-testid="ayapro-metier-search" /></div>
            {shownMetiers.map((m) => (
              <button key={m.id} className={`ap-metier ${metier === m.id ? "active" : ""}`} onClick={() => { setMetier(m.id); setQ(""); go("form"); }} data-testid={`ayapro-metier-${m.id}`}>
                <span className="ic">{m.icon}</span>
                <div><h4>{m.label}</h4><p>{m.sub}</p></div>
                <ChevronRight size={16} style={{ marginLeft: "auto", color: "#9CA3AF", flexShrink: 0 }} />
              </button>
            ))}
          </div>
        )}

        {/* ── Formulaire (Réf 4 : Infos → Activité → Vérification → Terminé) ── */}
        {step === "form" && (
          <div data-testid="ayapro-step-form">
            <h1 className="ap-h1" style={{ textAlign: "left" }}>Créez votre compte pro</h1>
            <p className="ap-sub" style={{ textAlign: "left", margin: "6px 0 0" }}>Renseignez vos informations pour rejoindre AYA PRO.</p>
            <Stepper steps={["Infos", "Activité", "Vérification", "Terminé"]} current={0} />
            {met && <div className="ap-badge" style={{ marginTop: 14 }}>{met.icon} {met.label} · {sec?.label}</div>}
            <div className="ap-card">
              <label className="ap-label" style={{ marginTop: 0 }}>Nom de l'entreprise ou du professionnel <span style={{ color: "#DC2626" }}>*</span></label>
              <div className="ap-field"><input value={f.business_name} onChange={(e) => setF((p) => ({ ...p, business_name: e.target.value }))} placeholder="Ex : Konan Coco" data-testid="ayapro-form-business" /></div>
              <label className="ap-label">Nom et prénom <span style={{ color: "#DC2626" }}>*</span></label>
              <div className="ap-field"><input value={f.owner_name} onChange={(e) => setF((p) => ({ ...p, owner_name: e.target.value }))} placeholder="Ex : Konan Kouassi" data-testid="ayapro-form-owner" /></div>
              <label className="ap-label">Téléphone <span style={{ color: "#DC2626" }}>*</span></label>
              <div style={{ display: "flex", gap: 8 }}>
                <span className="ap-field ap-prefix" style={{ flexShrink: 0, padding: "0 12px" }}>
                  <span className="ap-flag"><span style={{ background: "#F77F00" }} /><span style={{ background: "#fff" }} /><span style={{ background: "#009E60" }} /></span> +225
                </span>
                <div className="ap-field" style={{ flex: 1 }}><input value={f.phone} onChange={(e) => setF((p) => ({ ...p, phone: e.target.value }))} placeholder="07 07 07 07 07" inputMode="tel" data-testid="ayapro-form-phone" /></div>
              </div>
              <label className="ap-label">Email (optionnel)</label>
              <div className="ap-field"><Mail size={15} color="#9CA3AF" /><input value={f.email} onChange={(e) => setF((p) => ({ ...p, email: e.target.value }))} placeholder="votre@email.com" type="email" data-testid="ayapro-form-email" /></div>
              <label className="ap-label">Mot de passe <span style={{ color: "#DC2626" }}>*</span></label>
              <div className="ap-field">
                <Lock size={15} color="#9CA3AF" />
                <input type={showPw ? "text" : "password"} value={f.password} onChange={(e) => setF((p) => ({ ...p, password: e.target.value }))} placeholder="Créer un mot de passe" autoComplete="new-password" data-testid="ayapro-form-password" />
                <button type="button" className="ap-eye" onClick={() => setShowPw((s) => !s)} aria-label="Afficher">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
              {f.password && <PwChecks pw={f.password} />}
              <label className="ap-check" style={{ marginTop: 15, alignItems: "flex-start" }}>
                <input type="checkbox" checked={f.terms} onChange={(e) => setF((p) => ({ ...p, terms: e.target.checked }))} data-testid="ayapro-form-terms" style={{ marginTop: 1 }} />
                <span style={{ fontSize: 11.5, fontWeight: 600, lineHeight: 1.45 }}>J'accepte les <a href="/cgu" target="_blank" rel="noreferrer" className="ap-link">conditions d'utilisation</a> de AYA PRO et la <a href="/confidentialite" target="_blank" rel="noreferrer" className="ap-link">politique de confidentialité</a>.</span>
              </label>
              <button className="ap-cta" disabled={!formOk || busy} onClick={register} data-testid="ayapro-form-submit">{busy ? "Création…" : "Continuer"} <ChevronRight size={16} /></button>
            </div>
          </div>
        )}

        {/* ── Vérification du téléphone ── */}
        {step === "verify" && (
          <div data-testid="ayapro-step-verify" style={{ textAlign: "center" }}>
            <span style={{ width: 70, height: 70, borderRadius: "50%", background: "#0E8A4F", display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: 14 }}><Phone size={30} color="#fff" /></span>
            <h1 className="ap-h1">Vérifiez votre numéro</h1>
            <p className="ap-sub">Un code à 6 chiffres a été envoyé au <b style={{ color: "#111827" }}>+225 {f.phone}</b>. Il expire dans 10 minutes.</p>
            <Stepper steps={["Infos", "Activité", "Vérification", "Terminé"]} current={2} />
            {devCode && (
              <div className="ap-info amber" style={{ textAlign: "left" }} data-testid="ayapro-dev-code-box">
                <div style={{ flex: 1 }}>
                  <b>MODE TEST — aucun fournisseur SMS configuré.</b> Le code réel est affiché ci-dessous (en production il sera envoyé par SMS) :
                  <div className="ap-devcode" data-testid="ayapro-dev-code">{devCode}</div>
                </div>
              </div>
            )}
            <div className="ap-otp" data-testid="ayapro-otp-inputs">
              {otp.map((v, i) => (
                <input key={i} ref={(el) => { otpRefs.current[i] = el; }} value={v} onChange={(e) => setOtpAt(i, e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Backspace" && !v && i > 0) otpRefs.current[i - 1]?.focus(); }}
                  inputMode="numeric" data-testid={`ayapro-otp-${i}`} />
              ))}
            </div>
            <button className="ap-cta" style={{ maxWidth: 380 }} disabled={otp.join("").length !== 6 || busy} onClick={verify} data-testid="ayapro-verify-submit">{busy ? "Vérification…" : "Vérifier mon numéro"} <ChevronRight size={16} /></button>
            <div style={{ marginTop: 14 }}><button className="ap-link" onClick={resend} data-testid="ayapro-verify-resend">Je n'ai pas reçu de code — renvoyer</button></div>
          </div>
        )}

        {/* ── Identifiants (Réf 2 : Sécurisez votre compte) ── */}
        {step === "credentials" && (
          <div data-testid="ayapro-step-credentials">
            <img src="/aya/ayapro/merchant-woman.jpg" alt="" className="ap-herophoto" style={{ maxHeight: 150 }} />
            <h1 className="ap-h1" style={{ textAlign: "left", fontSize: 24 }}>Sécurisez votre compte</h1>
            <p className="ap-sub" style={{ textAlign: "left", margin: "6px 0 0" }}>Définissez vos identifiants personnels pour accéder à votre espace AYA PRO en toute sécurité.</p>
            <Stepper steps={["Vérification", "Identifiants", "Terminé"]} current={1} />
            <div className="ap-card">
              <label className="ap-label" style={{ marginTop: 0 }}>Votre numéro de téléphone</label>
              <div className="ap-field" style={{ background: "#F8FAF9" }}>
                <Phone size={15} color="#6B7280" />
                <span className="ap-flag"><span style={{ background: "#F77F00" }} /><span style={{ background: "#fff" }} /><span style={{ background: "#009E60" }} /></span>
                <input value={`+225 ${f.phone}`} readOnly data-testid="ayapro-cred-phone" style={{ fontWeight: 800 }} />
                <span className="ap-badge" data-testid="ayapro-cred-verified-badge"><CheckCircle2 size={13} /> Numéro vérifié</span>
              </div>
              <label className="ap-label">Votre email (optionnel)</label>
              <div className="ap-field"><Mail size={15} color="#9CA3AF" /><input value={cred.email} onChange={(e) => setCred((p) => ({ ...p, email: e.target.value }))} placeholder="votre@email.com" type="email" data-testid="ayapro-cred-email" /></div>
              <label className="ap-label">Créez votre mot de passe</label>
              <div className="ap-field">
                <Lock size={15} color="#9CA3AF" />
                <input type={showCred ? "text" : "password"} value={cred.pw} onChange={(e) => setCred((p) => ({ ...p, pw: e.target.value }))} placeholder="Au moins 8 caractères" autoComplete="new-password" data-testid="ayapro-cred-password" />
                <button type="button" className="ap-eye" onClick={() => setShowCred((s) => !s)} aria-label="Afficher">{showCred ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
              <div className="ap-strength">
                {[1, 2, 3].map((n) => <span key={n} className={`seg ${score >= n ? `on${score}` : ""}`} />)}
                <span style={{ fontSize: 11, fontWeight: 800, color: score >= 3 ? "#0E8A4F" : score === 2 ? "#B45309" : "#F0590B", flexShrink: 0 }}>
                  Sécurité : {score >= 3 ? "Forte" : score === 2 ? "Moyenne" : "Faible"}
                </span>
              </div>
              <label className="ap-label">Confirmez votre mot de passe</label>
              <div className="ap-field">
                <Lock size={15} color="#9CA3AF" />
                <input type={showCred ? "text" : "password"} value={cred.pw2} onChange={(e) => setCred((p) => ({ ...p, pw2: e.target.value }))} placeholder="Retapez votre mot de passe" autoComplete="new-password" data-testid="ayapro-cred-password2" />
              </div>
              <PwChecks pw={cred.pw} />
              <button className="ap-cta" disabled={!pwValid(cred.pw) || cred.pw !== cred.pw2 || busy} onClick={saveCredentials} data-testid="ayapro-cred-submit">{busy ? "Enregistrement…" : "Enregistrer mes identifiants"} <ChevronRight size={16} /></button>
            </div>
          </div>
        )}

        {/* ── Lien d'activation (Réf 8 : Bienvenue sur AYA PRO) ── */}
        {step === "activation" && activation && (
          <div data-testid="ayapro-step-activation">
            <img src="/aya/ayapro/merchant-woman.jpg" alt="" className="ap-herophoto" style={{ maxHeight: 150 }} />
            <h1 className="ap-h1" style={{ textAlign: "left", fontSize: 24 }}>Bienvenue sur AYA PRO !</h1>
            <p className="ap-sub" style={{ textAlign: "left", margin: "6px 0 0" }}>Votre espace professionnel est prêt. Activez votre compte pour commencer à gérer votre activité et recevoir des clients sur AYA.</p>
            <div className="ap-card" style={{ background: "#F0FBF4", border: "1px solid #D7EEDF" }}>
              <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 900 }}>Compte créé avec succès</h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6B7280" }}>Nous avons associé ce compte au numéro suivant :</p>
              <div className="ap-field" style={{ marginTop: 11, background: "#fff" }}>
                <Phone size={15} color="#6B7280" />
                <input value={activation.phone} readOnly data-testid="ayapro-activation-phone" style={{ fontWeight: 800 }} />
                <span className="ap-badge"><CheckCircle2 size={13} /> Numéro vérifié</span>
              </div>
            </div>
            <div className="ap-grid2" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
              {[["🏪", "Gérez vos produits et services"], ["👥", "Recevez des clients et des commandes"], ["📊", "Suivez votre activité en temps réel"]].map(([ic, t]) => (
                <div key={t} style={{ textAlign: "center" }}>
                  <span style={{ width: 52, height: 52, borderRadius: 15, background: "#E8F8EF", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{ic}</span>
                  <div style={{ fontSize: 10.5, fontWeight: 800, color: "#374151", marginTop: 6, lineHeight: 1.35 }}>{t}</div>
                </div>
              ))}
            </div>
            <div className="ap-info blue">
              <span className="ic"><ShieldCheck size={17} /></span>
              <div><b>Lien sécurisé.</b> Ce lien est personnel et valable pendant {activation.hours} heures. Il vous permet d'activer votre compte en toute sécurité. Usage unique.</div>
            </div>
            <div className="ap-info amber" data-testid="ayapro-activation-dev-box">
              <div style={{ flex: 1 }}>
                <b>MODE TEST — aucun canal email/SMS configuré.</b> Votre lien d'activation personnel est affiché ici (en production il sera envoyé par SMS/WhatsApp) :
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, background: "#fff", borderRadius: 10, padding: "8px 11px", border: "1px solid #FDE68A" }}>
                  <code style={{ fontSize: 10.5, wordBreak: "break-all", flex: 1, color: "#374151" }} data-testid="ayapro-activation-link">{window.location.origin}{activation.path}</code>
                  <button className="ap-link" onClick={copyLink} style={{ flexShrink: 0, display: "inline-flex", gap: 4, alignItems: "center" }} data-testid="ayapro-activation-copy"><Copy size={13} /> Copier</button>
                </div>
              </div>
            </div>
            <button className="ap-cta" onClick={() => { localStorage.removeItem(DRAFT_KEY); navigate(activation.path); }} data-testid="ayapro-activation-go">Activer mon compte <ChevronRight size={17} /></button>
            <div style={{ textAlign: "center", marginTop: 13 }}><button className="ap-link" onClick={() => toast.info(`Compte associé au ${activation.phone}. Pour changer de numéro, recommencez l'inscription.`)} data-testid="ayapro-activation-wrongnum" style={{ textDecoration: "underline" }}>Ce n'est pas mon numéro ?</button></div>
          </div>
        )}
      </div>
    </div>
  );
}
